import {
  useEffect, useState, useMemo, memo
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  alpha
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useHistory } from 'react-router-dom';
import ROUTE from 'src/utils/routeConstants';
import { useDispatch, useSelector } from 'react-redux';
import { getCardList } from 'src/redux/cardSlice';
import Loader from 'src/common/Loader';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import { cardTableHeaders, getAlignByColumnId } from 'src/utils/tableHeaders';
import CustomPagination from 'src/common/CustomPagination';
import { getFuelQuantity, getSelectedSiteFilterValue } from 'src/utils/getInitials';
import {
  getTableActionIconButtonSx,
  TABLE_ACTION_ICON_SX
} from 'src/common/tableActionStyles';
import { useTableColumnPreferences } from 'src/common/TableColumnVisibilityMenu';
import VehicleDetailsDialog from './VehicleDetailsDialog';
import SiteDetailsDialog from './SiteDetailsDialog';

/* ---------- Status Pill ---------- */
const StatusPill = ({ active }) => (
  <Chip
    label={active ? 'Active' : 'Inactive'}
    color={active ? 'success' : 'error'}
    size="small"
    variant="outlined"
    sx={{ fontWeight: 600 }}
  />
);

StatusPill.propTypes = {
  active: PropTypes.bool.isRequired
};

const RelatedEntityValue = ({
  value, id, label, detailType
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const displayValue = value || 'N/A';
  if (!id) return displayValue;

  const openDetails = () => setIsOpen(true);

  const handleTriggerKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openDetails();
    }
  };

  return (
    <>
      <Box
        component="span"
        aria-label={`Open ${label} details for ${displayValue}`}
        role="button"
        tabIndex={0}
        onClick={openDetails}
        onKeyDown={handleTriggerKeyDown}
        sx={{
          borderRadius: 1,
          color: 'inherit',
          cursor: 'pointer',
          fontWeight: 600,
          px: 0.5,
          '&:hover, &:focus-visible': {
            backgroundColor: 'rgba(15, 23, 42, 0.06)',
            outline: 'none'
          }
        }}
      >
        {displayValue}
      </Box>
      {detailType === 'vehicle' ? (
        <VehicleDetailsDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          vehicleId={id}
          vehicleNumber={displayValue}
        />
      ) : (
        <SiteDetailsDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          siteId={id}
          siteName={displayValue}
        />
      )}
    </>
  );
};

RelatedEntityValue.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string.isRequired,
  detailType: PropTypes.oneOf(['vehicle', 'site']).isRequired
};

/* ---------- Mobile Card ---------- */
const MobileCardView = memo(({ card, onViewClick, onEditClick }) => (
  <Card
    sx={{
      mb: 2,
      p: 2,
      borderRadius: 2,
      boxShadow: 'none',
      border: '1px solid',
      borderColor: 'divider'
    }}
  >
    <Typography variant="subtitle1" fontWeight={700}>
      {card.uid || 'N/A'}
    </Typography>

    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary" component="div">
        Vehicle:
        {' '}
        <RelatedEntityValue
          value={card.vehicle_number}
          id={card.vehicle_id}
          label="vehicle"
          detailType="vehicle"
        />
      </Typography>

      <Typography variant="body2" color="text.secondary" component="div">
        Site:
        {' '}
        <RelatedEntityValue
          value={card.site}
          id={card.site_id}
          label="site"
          detailType="site"
        />
      </Typography>

      <Typography variant="body2" color="text.secondary">
        Type:
        {' '}
        {card.card_type || 'N/A'}
      </Typography>

      <Typography variant="body2" color="text.secondary">
        Limit:
        {' '}
        {getFuelQuantity(card.limit_quantity, card.limit_quantity_gallons) || '0 L'}
      </Typography>
    </Box>

    <Box sx={{ mt: 2 }}>
      <StatusPill active={card.active} />
    </Box>

    <Button
      fullWidth
      size="small"
      variant="outlined"
      sx={{ mt: 2 }}
      onClick={() => onViewClick(card.id)}
    >
      View Details
    </Button>
    <Button
      fullWidth
      size="small"
      variant="contained"
      sx={{ mt: 1 }}
      startIcon={<EditOutlinedIcon />}
      onClick={() => onEditClick(card.id)}
    >
      Edit Card
    </Button>
  </Card>
));

MobileCardView.propTypes = {
  card: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    uid: PropTypes.string,
    vehicle_number: PropTypes.string,
    vehicle_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    site: PropTypes.string,
    site_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    card_type: PropTypes.string,
    limit_quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    limit_quantity_gallons: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    active: PropTypes.bool
  }).isRequired,
  onViewClick: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired
};

MobileCardView.displayName = 'MobileCardView';

/* ---------- Main Component ---------- */
const CardListTable = ({
  refreshData, searchText, searchFilters = {}, siteFilter, columnPreferences, onEditCard
}) => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();

  const { cardList, totalCount, loading } = useSelector((state) => state.cards);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [limit, setLimit] = useState(25);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const internalColumnPreferences = useTableColumnPreferences('cards', cardTableHeaders, !columnPreferences);
  const activeColumnPreferences = columnPreferences || internalColumnPreferences;
  const {
    visibleColumns: visibleCardHeaders
  } = activeColumnPreferences;
  const cardAlignById = useMemo(() => getAlignByColumnId(visibleCardHeaders), [visibleCardHeaders]);
  const selectedSite = getSelectedSiteFilterValue(siteFilter);

  useEffect(() => {
    setPage(0);
    dispatch(
      getCardList({
        limit,
        pageNo: 1,
        params: { ...searchFilters, search: searchText, site: selectedSite }
      })
    );
  }, [dispatch, limit, refreshData, searchText, searchFilters, selectedSite]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(
      getCardList({
        limit,
        pageNo: newPage + 1,
        params: { ...searchFilters, search: searchText, site: selectedSite }
      })
    );
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
  };

  const viewCardInfo = (cardId) => {
    history.push(`${ROUTE.cards}/${cardId}`, { viewOnly: true });
  };

  const editCardInfo = (cardId) => {
    onEditCard(cardId);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const displayData = cardList?.slice().sort(getComparator(order, orderBy)) || [];

  const renderCardCellValue = (card, columnId) => {
    const cardValues = {
      id: card.id,
      uid: card.uid,
      vehicle_number: (
        <RelatedEntityValue
          value={card.vehicle_number}
          id={card.vehicle_id}
          label="vehicle"
          detailType="vehicle"
        />
      ),
      site: (
        <RelatedEntityValue
          value={card.site}
          id={card.site_id}
          label="site"
          detailType="site"
        />
      ),
      card_category: card.card_category || 'N/A',
      per_filling_limit: getFuelQuantity(card.per_filling_limit, card.per_filling_limit_gallons) || '0 L',
      per_day_limit: getFuelQuantity(card.per_day_limit, card.per_day_limit_gallons) || '0 L',
      limit_quantity: getFuelQuantity(card.limit_quantity, card.limit_quantity_gallons) || '0 L',
      fill_limit_type: card.fill_limit_type || 'N/A',
      active: <StatusPill active={card.active} />
    };
    return cardValues[columnId] ?? card[columnId] ?? 'N/A';
  };

  if (loading) return <Loader />;

  return (
    <>
      {isMobile ? (
        <>
          {displayData.map((card) => (
            <MobileCardView
              key={card.id}
              card={card}
              onViewClick={viewCardInfo}
              onEditClick={editCardInfo}
            />
          ))}

          <CustomPagination
            page={page}
            rowsPerPage={limit}
            count={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            showRowsPerPage
          />
        </>
      ) : (
        <Box
          sx={{
            width: '100%',
            borderRadius: 2,
            bgcolor: '#FFFFFF',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              minHeight: 0,
              flex: 1,
              overflow: 'auto',
              '& .MuiTableCell-stickyHeader': {
                backgroundColor: '#F9FAFB',
                borderBottom: `1px solid ${theme.palette.divider}`,
                zIndex: 2
              },
              '& .MuiTableHead-root .MuiTableCell-root:last-of-type': {
                position: 'sticky',
                right: 0,
                zIndex: 3,
                backgroundColor: '#F9FAFB',
                boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
              }
            }}
          >
            <Table stickyHeader>
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                headCells={visibleCardHeaders}
                onRequestSort={handleRequestSort}
                sx={{
                  backgroundColor: '#F9FAFB',
                  borderBottom: `1px solid ${theme.palette.divider}`
                }}
              />
              <TableBody>
                {displayData.length > 0 ? (
                  displayData.map((card) => (
                    <TableRow
                      hover
                      key={card.id}
                      sx={{
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                        '&:last-child': { borderBottom: 'none' },
                        '&:hover': { backgroundColor: '#F9FAFB' }
                      }}
                    >
                      {visibleCardHeaders
                        .filter((column) => column.id !== 'action')
                        .map((column) => (
                          <TableCell align={cardAlignById[column.id]} key={column.id}>
                            {renderCardCellValue(card, column.id)}
                          </TableCell>
                        ))}
                      <TableCell
                        align="center"
                        sx={{
                          position: 'sticky',
                          right: 0,
                          zIndex: 1,
                          backgroundColor: '#FFFFFF',
                          boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'nowrap'
                          }}
                        >
                          <Tooltip title="View Details">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                viewCardInfo(card.id);
                              }}
                              size="medium"
                              aria-label="View details"
                              sx={getTableActionIconButtonSx(theme)}
                            >
                              <VisibilityOutlinedIcon sx={TABLE_ACTION_ICON_SX} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Card">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                editCardInfo(card.id);
                              }}
                              size="medium"
                              aria-label="Edit card"
                              sx={getTableActionIconButtonSx(theme)}
                            >
                              <EditOutlinedIcon sx={TABLE_ACTION_ICON_SX} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={visibleCardHeaders.length} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">
                        No Cards Found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          <Divider sx={{ flexShrink: 0 }} />

          <Box sx={{ p: 1, flexShrink: 0, bgcolor: '#FFFFFF' }}>
            <CustomPagination
              page={page}
              rowsPerPage={limit}
              count={totalCount}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleLimitChange}
              showRowsPerPage
            />
          </Box>
        </Box>
      )}
    </>
  );
};

CardListTable.propTypes = {
  refreshData: PropTypes.bool,
  searchText: PropTypes.string,
  searchFilters: PropTypes.object,
  siteFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onEditCard: PropTypes.func.isRequired,
  columnPreferences: PropTypes.shape({
    hiddenColumnIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    visibleColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
    onToggleColumn: PropTypes.func.isRequired
  })
};

export default CardListTable;
